import { MenuItem, TextField } from "@mui/material";
import { useState } from "react";
import { CatalogService } from "@services/catalog/catalogService";
import { CatalogCoreFieldSample, CORE_FIELD_SAMPLE_ROLES } from "@models/catalog.model";
import { FormDialog } from "./FormDialog";

// Per-vertical doc-copy (sample + description) for a standard core field — the identity key
// or a contact channel (MPN-1233). One row per (vertical, role); the field-map editor and the
// instructions generator read these instead of asking each config to re-type them.
export function CoreFieldSampleDialog({
  verticalId, existingRoles, initial, onClose, onSaved,
}: {
  verticalId: number;
  existingRoles: string[]; // roles already used in this vertical, disabled in create mode
  initial: CatalogCoreFieldSample | null;
  onClose: () => void;
  onSaved: () => void;
}) {
  const svc = new CatalogService();
  const [role, setRole] = useState(initial?.role ?? "");
  const [sample, setSample] = useState(initial?.sample ?? "");
  const [description, setDescription] = useState(initial?.description ?? "");
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const save = async () => {
    setSaving(true);
    setError(null);
    const payload = { vertical_id: verticalId, role, sample, description };
    try {
      if (initial) await svc.updateCoreFieldSample(initial.id, payload);
      else await svc.createCoreFieldSample(payload);
      onSaved();
    } catch (e) {
      setError(e instanceof Error ? e.message : "Save failed");
    } finally {
      setSaving(false);
    }
  };

  return (
    <FormDialog
      open
      title={initial ? "Edit core field sample" : "New core field sample"}
      onClose={onClose}
      onSave={save}
      saving={saving}
      error={error}
      canSave={!!role.trim()}
    >
      <TextField select label="Field" value={role}
        onChange={(e) => setRole(e.target.value)}
        // Role is stable once set (it's the lookup key); editing an existing row keeps it fixed.
        disabled={!!initial} helperText="The standard core field this doc-copy describes" size="small">
        {CORE_FIELD_SAMPLE_ROLES.map((r) => (
          <MenuItem key={r} value={r} disabled={!initial && existingRoles.includes(r)}>
            {r}
          </MenuItem>
        ))}
      </TextField>
      <TextField label="Sample value" value={sample} onChange={(e) => setSample(e.target.value)}
        helperText="A canonical example for the customer instructions doc, e.g. 90210, jane@school.edu" size="small" />
      <TextField label="Description" value={description} onChange={(e) => setDescription(e.target.value)}
        helperText="What this column is — shown in the instructions doc" size="small" multiline minRows={2} />
    </FormDialog>
  );
}
